import { Theme, css } from '@emotion/react';
import { useCombobox } from 'downshift';
import { FC, useRef, useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { StaticInputProps } from '~src/address/delivery-address-form/config/static-props';
import { StandardInput } from '~src/address/delivery-address-form/inputs/standard-input';
import { AddressFormFields } from '~src/address/delivery-address-form/types';
import { Icon, Text } from '~src/commons/components';
import { useFormFieldScroll } from '~src/commons/utils/hooks/use-form-field-scroll';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { fontStyles } from '~src/theme/fontStyles';

interface SearchableSelectProps extends Partial<StaticInputProps> {
  name: keyof Pick<AddressFormFields, 'region' | 'subregion'>;
  label: string;
  validationRules: RegisterOptions;
  options: { value: string; label: string }[];
}

export const SearchableSelect: FC<SearchableSelectProps> = ({
  name,
  label,
  options,
  validationRules,
}) => {
  const { setValue, getValues, trigger, getFieldState } = useFormContext<AddressFormFields>();
  const [items, setItems] = useState<{ value: string; label: string }[]>(options);

  // ref to store the input value for further check if the user pasted in or autofilled a value
  const inputValueRef = useRef<string | null>(null);

  const ref = useRef<HTMLInputElement>(null);
  const { tICU } = useTranslations();
  const formValues = getValues(name);

  useFormFieldScroll(name, ref);

  useDeepCompareEffect(() => {
    setItems(options);
  }, [options]);

  const getExactValueFilter = () => {
    return options.filter(option => {
      const lowerCasedInputValue = option.value.toLowerCase();
      return (value: { value: string; label: string }) => {
        const isInputValueInOptions =
          value.value.toLowerCase() === lowerCasedInputValue ||
          value.label.toLowerCase() === lowerCasedInputValue;

        const isPreviousValueEmpty = inputValueRef.current === '' || inputValueRef.current === null;
        return isInputValueInOptions && isPreviousValueEmpty;
      };
    });
  };

  const getValueFilter = () => {
    return options.filter(option => {
      const lowerCasedInputValue = option.value.toLowerCase();

      return (value: { value: string; label: string }) => {
        return (
          !option.value ||
          value.value.toLowerCase().includes(lowerCasedInputValue) ||
          value.label.toLowerCase().includes(lowerCasedInputValue)
        );
      };
    });
  };

  const {
    selectedItem,
    isOpen,
    getComboboxProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    closeMenu,
    openMenu,
    getItemProps,
  } = useCombobox({
    defaultIsOpen: false,
    onSelectedItemChange() {
      if (getFieldState(name)?.error) {
        trigger(name);
      }
    },
    onInputValueChange({ inputValue }) {
      if (!inputValue) {
        inputValueRef.current = '';
        setItems(options);
        return;
      }
      const pastedInOrAutofilledValue = getExactValueFilter();
      if (pastedInOrAutofilledValue.length === 1) {
        setValue(name, pastedInOrAutofilledValue[0].value);
        inputValueRef.current = pastedInOrAutofilledValue[0].label;
        closeMenu();
      }
      setItems(getValueFilter());
      inputValueRef.current = inputValue;
    },
    items,
    defaultInputValue: formValues,
    itemToString(item) {
      return item?.label ?? '';
    },
  });

  const handleClick = () => {
    openMenu();
  };

  return (
    <div style={{ position: 'relative' }} {...getComboboxProps()}>
      <StandardInput
        ref={ref}
        {...getInputProps({
          refKey: 'ref',
        })}
        name={name}
        label={label}
        onInputClick={handleClick}
        validationRules={{
          ...validationRules,
          validate: {
            validateSelectInputValue: value => {
              if (selectedItem && getFieldState(name)?.error) {
                trigger(name);
              }
              const inputSameAsOption = value && options.find(item => item.label === value);
              if (!inputSameAsOption || !selectedItem) {
                return tICU(`widget.ADDRESS_MODAL.${name.toUpperCase()}_REQUIRED_VALIDATION`);
              }
            },
          },
        }}
      />
      <ul css={styles.list(isOpen && !!items.length)} {...getMenuProps()}>
        {isOpen ? (
          <>
            {!formValues && (
              <li key="disabled-no-value" css={styles.disabledItem}>
                <div css={styles.textWrapper}>
                  <Text css={styles.mainText}>
                    {tICU('widget.delivery_address.SEARCHABLE_SELECT_OPTION_PLACEHOLDER')}
                  </Text>
                </div>
              </li>
            )}
            {formValues &&
              items.slice(0, 5).map((item, index) => {
                return (
                  <li
                    css={styles.listItem({
                      isFirstChild: index === 0,
                      isHighlighted: highlightedIndex === index,
                    })}
                    key={item.value}
                    {...getItemProps({ item: item, index })}
                  >
                    <div css={styles.textWrapper}>
                      <Text css={styles.mainText}>{item.label}</Text>
                    </div>
                    <Icon type="arrowTopLeft" css={styles.arrowIcon} />
                  </li>
                );
              })}
          </>
        ) : null}
      </ul>
    </div>
  );
};

const styles = {
  list: (visible: boolean) => (theme: Theme) =>
    css({
      position: 'absolute',
      top: '60px',
      display: visible ? 'block' : 'none',
      background: 'white',
      overflow: 'hidden',
      padding: 0,
      margin: 0,
      '&:empty': {
        border: 0,
        opacity: 0,
      },
      ...(visible && {
        boxShadow: '0px 2px 8px 0px #0000001A, 2px 2px 8px 0px #0000001A',
        borderRadius: '3px',
        border: `1px solid ${theme.neutral91}`,
        width: '100%',
        zIndex: 999,
        background: 'white',
        position: 'absolute',
      }),
    }),
  arrowIcon: css({ marginLeft: 'auto' }),
  listWrapper: css({
    width: '100%',
    zIndex: 999,
    background: 'white',
    position: 'absolute',
    borderRadius: '3px',
  }),
  mainText: (theme: Theme) =>
    css({
      marginBottom: '2px',
      ...fontStyles.px16_20,
      color: theme.darkGrey,
    }),
  secondaryText: css({
    ...fontStyles.px14_20,
  }),
  listItem: ({
    isFirstChild,
    isLastChild = false,
    isHighlighted,
  }: {
    isFirstChild: boolean;
    isLastChild?: boolean;
    isHighlighted: boolean;
  }) =>
    css({
      background: isHighlighted ? 'hsl(0, 0%, 98%)' : 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      height: isLastChild ? '40px' : '52px',
      padding: '8px 20px 8px 16px',
      marginTop: isFirstChild ? '8px' : '4px',
      marginBottom: 0,
    }),
  disabledItem: css({
    background: 'hsl(0, 0%, 98%)',
    display: 'flex',
    alignItems: 'center',
    minHeight: '84px',
    padding: '16px',
    marginBottom: 0,
    cursor: 'not-allowed',
    ...fontStyles.px14_20,
  }),
  textWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    fontVariationSettings: "'wght' 400",
  }),
  openManualAddressButton: css({
    ...fontStyles.px12_16,
    fontVariationSettings: '"wght" 500',
    marginLeft: '4px',
  }),
};
